import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "@/store/store";
import type {
  BootstrapQuestion,
  BootstrapQuestionOption,
  BootstrapQuestionRollup,
  BootstrapQuestionRollupBand,
} from "@/interfaces/v2Analytics";
import { agreedValue, labelVariants } from "@/utils/catalogLabels";
import type {
  Question,
  QuestionOption,
  Rollup,
  RollupOption,
} from "@/interfaces/questions";

/**
 * Structural match for the legacy questions entity-adapter state
 * (`EntityState<Question, string>` keyed by questionKey). All legacy
 * consumers (picker, DataCardLists, exports, saveView, selectionListener)
 * keep reading this exact shape.
 */
export type QuestionsAdapter = {
  ids: string[];
  entities: Record<string, Question>;
};

const EMPTY_ADAPTER: QuestionsAdapter = { ids: [], entities: {} };

/**
 * Deterministic fallback palette (same hex values as `colorCodes()` in
 * Rollups.tsx) — bootstrap carries no colors yet, so options/bands get a
 * stable color by position until the backend adds `color` to the embed.
 */
export const QUESTION_FALLBACK_COLORS = [
  "#FF5733",
  "#33FFCE",
  "#A45EEB",
  "#FAD7A0",
  "#8E44AD",
  "#2ECC71",
  "#9B59B6",
  "#E74C3C",
  "#3498DB",
  "#F39C12",
];

/** Available when `markets` is absent/empty, else every selected market must be covered. */
function isAvailableInMarkets(
  markets: number[] | undefined,
  marketIds: number[],
): boolean {
  if (!markets || markets.length === 0) return true;
  return marketIds.every((m) => markets.includes(m));
}

/**
 * An option's label for the selected markets: the per-market override when
 * every selected market resolves to the same text, else the canonical label.
 */
function resolveOptionLabel(
  option: BootstrapQuestionOption,
  marketIds: number[],
): string {
  return agreedValue(labelVariants(option, marketIds)) ?? option.label;
}

function bandType(source: BootstrapQuestionRollup["source"]): string {
  return source === "USER" ? "USER_ROLLUP" : "SYSTEM_ROLLUP";
}

/**
 * Scheme → legacy `Rollup`. Shared by the bootstrap catalog and the
 * create/update scheme responses, which return the same scheme shape.
 *
 * Bands may reference options three ways (all tried):
 *   1. sourceValues: ["5", "4"]  — raw response values (SYSTEM rollups)
 *   2. sourceValues: ["1001"]    — option IDs as strings (some USER backends)
 *   3. sourceOptionIds / optionIds: [1001] — option IDs as numbers
 * `resolveOption` maps one such reference to an option, or undefined. It
 * is told which field the reference came from, so an id is never matched
 * against option values (id 1 must not resolve to an option whose value is
 * "1").
 *
 * The first band carries the rollup's id (color-override key) and color.
 */
/**
 * A band's option references, option ids first. USER bands carry both
 * `sourceOptionIds` and `sourceValues`, and the ids are exact: on multi-select
 * questions every option can share the same value (e.g. each bank is "1"), so
 * values would collapse a multi-option band to a single option. SYSTEM bands
 * carry values only.
 */
export type BandOptionRefKind = "id" | "value";

function bandOptionRefs(
  band: BootstrapQuestionRollupBand,
): { ref: string; kind: BandOptionRefKind }[] {
  const ids = band.sourceOptionIds?.length
    ? band.sourceOptionIds
    : (band as any).optionIds;
  if (ids?.length)
    return ids.map((id: string | number) => ({ ref: String(id), kind: "id" }));
  return (band.sourceValues ?? []).map((ref) => ({ ref, kind: "value" }));
}

export function mapSchemeToRollup(
  scheme: BootstrapQuestionRollup,
  resolveOption: (
    ref: string,
    kind: BandOptionRefKind,
  ) => RollupOption | undefined,
  fallbackColor: string,
  colorOverrides: Record<string, string> = {},
): Rollup {
  const options = scheme.bands.flatMap((band) => {
    const refs = bandOptionRefs(band);
    return refs
      .map(({ ref, kind }) => resolveOption(ref, kind))
      .filter((o): o is RollupOption => o != null);
  });

  const firstBandId = scheme.bands[0]?.id;
  return {
    accessType: scheme.source,
    rollupColor:
      colorOverrides[`band-${firstBandId}`] ??
      scheme.bands[0]?.color ??
      fallbackColor,
    rollupId: firstBandId,
    rollupKey: schemeIdentifier(scheme),
    rollupLabel: scheme.schemeLabel ?? scheme.bands[0]?.label ?? "",
    type: bandType(scheme.source),
    options,
    bandKeys: scheme.bands.map((band) => band.key).filter(Boolean),
  };
}

/**
 * Path segment for PUT/DELETE `/v2/questions/scheme/{questionCode}/{scheme}`:
 * the scheme key when present, else the scheme id (multi-band USER create
 * responses carry only `id`).
 */
export function schemeIdentifier(
  scheme: Pick<BootstrapQuestionRollup, "schemeKey" | "id">,
): string {
  return scheme.schemeKey || (scheme.id != null ? String(scheme.id) : "");
}

/**
 * USER scheme → one legacy `Rollup` per band. A USER scheme holds all of a
 * question's user rollups as bands, each with its own id, key, label and
 * color, so each band is a separately selectable rollup. Legacy one-band
 * USER schemes map to a single rollup, same as before.
 *
 * `rollupKey` is the scheme identifier (shared by all bands) — it is what
 * PUT/DELETE address; `bandKeys` holds the band's own key (card colors and
 * analytics Series keys are matched on it).
 */
export function mapSchemeToBandRollups(
  scheme: BootstrapQuestionRollup,
  resolveOption: (
    ref: string,
    kind: BandOptionRefKind,
  ) => RollupOption | undefined,
  fallbackColor: (bandIndex: number) => string,
  colorOverrides: Record<string, string> = {},
): Rollup[] {
  const rollupKey = schemeIdentifier(scheme);
  return scheme.bands.map((band, i) => {
    const refs = bandOptionRefs(band);
    return {
      accessType: scheme.source,
      rollupColor:
        colorOverrides[`band-${band.id}`] ?? band.color ?? fallbackColor(i),
      rollupId: band.id,
      rollupKey,
      rollupLabel: band.label ?? scheme.schemeLabel ?? "",
      type: bandType(scheme.source),
      options: refs
        .map(({ ref, kind }) => resolveOption(ref, kind))
        .filter((o): o is RollupOption => o != null),
      bandKeys: band.key ? [band.key] : [],
    };
  });
}

/**
 * Pure transformer: bootstrap catalog → legacy Question shape.
 *  - market-filters questions and options (absence ⇒ available);
 *  - flattens rollup bands into one selectable legacy `Rollup` per band,
 *    with `type` derived from the scheme `source` (reference-mode picks);
 *  - dedups on `code` (matrix cells share `id` — never dedup on id);
 *  - sorts matrix cells by rowIndex within their fan-out family;
 *  - applies session color overrides and the user-rollups override.
 */
export function transformBootstrapQuestions(
  catalog: BootstrapQuestion[] | null,
  marketIds: number[],
  colorOverrides: Record<string, string>,
  userRollupsOverride: Record<string, Rollup[]>,
): QuestionsAdapter {
  if (!catalog || catalog.length === 0) return EMPTY_ADAPTER;

  // Dedup on code (keep first) + market filter.
  const seen = new Set<string>();
  const kept: { q: BootstrapQuestion; idx: number }[] = [];
  catalog.forEach((q, idx) => {
    if (seen.has(q.code)) return;
    if (!isAvailableInMarkets(q.markets, marketIds)) return;
    seen.add(q.code);
    kept.push({ q, idx });
  });

  // Stable order: catalog order between families; rowIndex within a
  // matrix family (family = shared registry question `id`).
  const familyFirstIdx = new Map<number, number>();
  kept.forEach(({ q, idx }) => {
    if (!familyFirstIdx.has(q.id)) familyFirstIdx.set(q.id, idx);
  });
  kept.sort((a, b) => {
    const fa = familyFirstIdx.get(a.q.id)!;
    const fb = familyFirstIdx.get(b.q.id)!;
    if (fa !== fb) return fa - fb;
    return (a.q.rowIndex ?? a.idx) - (b.q.rowIndex ?? b.idx);
  });

  const ids: string[] = [];
  const entities: Record<string, Question> = {};

  kept.forEach(({ q }, position) => {
    // Compute a single optionColorById map from the UNFILTERED q.options list
    // (by index). This ensures every option has one stable color everywhere —
    // both in the primary options array and nested in band options.
    const optionColorById = new Map(
      q.options.map((o, i) => [
        o.id,
        colorOverrides[`option-${o.id}`] ??
          o.color ??
          QUESTION_FALLBACK_COLORS[i % QUESTION_FALLBACK_COLORS.length],
      ]),
    );

    const options: QuestionOption[] = q.options
      .filter((o) => isAvailableInMarkets(o.markets ?? q.markets, marketIds))
      .map((o) => ({
        optionId: o.id,
        optionKey: o.key,
        optionValue: Number(o.value) || 0,
        optionLabel: resolveOptionLabel(o, marketIds),
        color: optionColorById.get(o.id)!,
        sequence: o.ordinal,
        type: "RESPONSE",
        markets: (o.markets ?? q.markets ?? []).map((id) => ({
          id,
          key: String(id),
          label: "",
        })),
      }));

    // Resolve band option references (values or ids) back to options for
    // display (picker secondaryOptions + Manage Roll-ups modal). Ids resolve
    // against ids only; values fall back to ids for backends that put option
    // ids in `sourceValues`.
    const optionByValue = new Map(q.options.map((o) => [o.value, o]));
    const optionById = new Map(q.options.map((o) => [String(o.id), o]));
    const resolveOption = (
      ref: string,
      kind: BandOptionRefKind,
    ): RollupOption | undefined => {
      const o =
        kind === "id"
          ? optionById.get(ref)
          : (optionByValue.get(ref) ?? optionById.get(ref));
      return o
        ? {
            optionId: o.id,
            optionKey: o.key,
            optionLabel: resolveOptionLabel(o, marketIds),
            optionValue: Number(o.value) || 0,
            color: optionColorById.get(o.id)!,
            sequence: o.ordinal,
            type: "RESPONSE",
          }
        : undefined;
    };
    // SYSTEM/ADMIN_CURATED schemes stay one rollup per scheme; USER schemes
    // fan out to one rollup per band. Fallback colors advance per produced
    // rollup so bands of one scheme don't share a color.
    let rollupIdx = 0;
    const nextFallback = () =>
      QUESTION_FALLBACK_COLORS[
        (q.options.length + rollupIdx++) % QUESTION_FALLBACK_COLORS.length
      ];
    const catalogRollups: Rollup[] = q.rollups.flatMap((scheme) =>
      scheme.source === "USER"
        ? mapSchemeToBandRollups(
            scheme,
            resolveOption,
            nextFallback,
            colorOverrides,
          )
        : [
            mapSchemeToRollup(
              scheme,
              resolveOption,
              nextFallback(),
              colorOverrides,
            ),
          ],
    );

    // The save response is authoritative for a question's USER rollups
    // within the session: keep non-USER schemes, replace USER ones. Their
    // nested option labels were captured in whichever market was active at
    // save time, so re-resolve them against the current markets.
    const override = userRollupsOverride[q.code];
    const rollups =
      override != null
        ? [
            ...catalogRollups.filter((r) => r.type !== "USER_ROLLUP"),
            ...override.map((r) => ({
              ...r,
              rollupColor: colorOverrides[`band-${r.rollupId}`] ?? r.rollupColor,
              options: r.options?.map((opt) => {
                const catalogOption = optionById.get(String(opt.optionId));
                return catalogOption
                  ? { ...opt, optionLabel: resolveOptionLabel(catalogOption, marketIds) }
                  : opt;
              }),
            })),
          ]
        : catalogRollups;

    // `displayLabel` is a shortened canonical label, so a market override
    // replaces it too rather than sitting behind it.
    const questionLabel = agreedValue(labelVariants(q, marketIds)) ?? q.label;
    const baseLabel =
      questionLabel !== q.label ? questionLabel : (q.displayLabel ?? q.label);
    const displayLabel =
      q.kind === "MATRIX_CELL" && q.groupLabel
        ? `${q.groupLabel} — ${baseLabel}`
        : baseLabel;

    const question: Question = {
      questionDisplayLabel: displayLabel,
      questionGroup: q.groupLabel ?? q.category ?? q.code,
      questionKey: q.code,
      questionLabel,
      questionSequence: position,
      rollups,
      options,
    };

    ids.push(q.code);
    entities[q.code] = question;
  });

  return { ids, entities };
}

/**
 * Memoized store-facing selector. Recomputes only when the catalog, the
 * overlays, or the selected market change.
 */
export const selectQuestionsAdapter = createSelector(
  [
    (state: RootState) => state.activeGroup.questionsCatalog.catalog,
    (state: RootState) => state.activeGroup.questionsCatalog.colorOverrides,
    (state: RootState) =>
      state.activeGroup.questionsCatalog.userRollupsOverride,
    (state: RootState) => state.userSelection.markets,
  ],
  (catalog, colorOverrides, userRollupsOverride, markets) =>
    transformBootstrapQuestions(
      catalog,
      markets ?? [],
      colorOverrides,
      userRollupsOverride,
    ),
);
